/**
 * routes/Decisions — the full committee decision log (TF.4). Every committee call the
 * engine has made, newest-first: ENTER / WAIT / REJECT with the arbiter's confidence and
 * a one-line reason, each row expandable (via DecisionItem) to the six role votes, the
 * rule-gate results and the final verdict. Filterable by symbol and by verdict so an
 * operator can audit, e.g., "every REJECT on ETHUSDT".
 *
 * Reads the paginated /api/decisions feed on a short poll and folds in the shell's
 * SSE-pushed latest decision, so a fresh call appears without waiting for the next tick.
 */
import { useMemo, useState } from 'react';

import { api } from '../api/client';
import type { DecisionRecord, DecisionsResponse, FinalVerdict } from '../api/types';
import { usePoll } from '../api/usePoll';
import { useShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { DecisionItem } from '../components/committee/DecisionItem';
import { ago } from '../lib/format';

type VerdictFilter = 'ALL' | FinalVerdict;

const VERDICT_FILTERS: readonly { key: VerdictFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'ENTER', label: 'Enter' },
  { key: 'WAIT', label: 'Wait' },
  { key: 'REJECT', label: 'Reject' },
];

/** Fold the SSE-pushed latest decision into the polled list, newest-first, deduped. */
function mergeDecisions(list: readonly DecisionRecord[], pushed: DecisionRecord | null): readonly DecisionRecord[] {
  if (!pushed || list.some((d) => d.id === pushed.id)) return list;
  return [pushed, ...list];
}

export function Decisions(): JSX.Element {
  const live = useShell();
  const [verdict, setVerdict] = useState<VerdictFilter>('ALL');
  const [symbol, setSymbol] = useState<string>('ALL');

  const feed = usePoll<DecisionsResponse>((signal) => api.decisions(signal, 100), { intervalMs: 15_000 });

  const all = useMemo(
    () => mergeDecisions(feed.data?.decisions ?? [], live.latestDecision),
    [feed.data, live.latestDecision],
  );

  // Symbols present in the current feed, for the symbol filter chips.
  const symbols = useMemo(() => Array.from(new Set(all.map((d) => d.symbol))).sort(), [all]);

  const filtered = useMemo(
    () =>
      all.filter(
        (d) => (verdict === 'ALL' || d.verdict === verdict) && (symbol === 'ALL' || d.symbol === symbol),
      ),
    [all, verdict, symbol],
  );

  const loading = feed.loading && !feed.data;

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Decisions</h1>
        <span className="page-head__sub">
          {feed.updatedAt ? `updated ${ago(feed.updatedAt)}` : 'loading…'}
        </span>
      </div>

      {feed.error && !feed.data && (
        <div className="banner-error section" role="alert">
          Decision feed unavailable: {feed.error}
        </div>
      )}

      <Card
        title="Committee log"
        hint={all.length ? `${filtered.length} of ${all.length}` : undefined}
        flush
      >
        <div className="filters" role="group" aria-label="Filter decisions">
          <div className="filters__group">
            <span className="filters__label">Verdict</span>
            <div className="seg" role="group" aria-label="Filter by verdict">
              {VERDICT_FILTERS.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  className={`seg__btn${verdict === v.key ? ' is-active' : ''}`}
                  aria-pressed={verdict === v.key}
                  onClick={() => setVerdict(v.key)}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="filters__group">
            <span className="filters__label">Symbol</span>
            <div className="seg" role="group" aria-label="Filter by symbol">
              <button
                type="button"
                className={`seg__btn${symbol === 'ALL' ? ' is-active' : ''}`}
                aria-pressed={symbol === 'ALL'}
                onClick={() => setSymbol('ALL')}
              >
                All
              </button>
              {symbols.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`seg__btn${symbol === s ? ' is-active' : ''}`}
                  aria-pressed={symbol === s}
                  onClick={() => setSymbol(s)}
                >
                  {s.replace('USDT', '')}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="state"><span className="spinner" aria-hidden /><span>Loading decisions…</span></div>
        ) : filtered.length === 0 ? (
          <div className="state">
            <span className="muted">
              {all.length === 0 ? 'No committee decisions yet.' : 'No decisions match these filters.'}
            </span>
          </div>
        ) : (
          <ul className="decision-list">
            {filtered.map((d) => (
              <DecisionItem key={d.id} decision={d} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
